import React from "react";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import PermissionGuard from "../PermissionGuard/PermissionGuard";
import "./PermissionExamples.scss";

const PermissionExamples: React.FC = () => {
  const {
    can,
    canAny,
    canAll,
    canCreateJob,
    canEditJob,
    canDeleteJob,
    canManageJobs,
    canManageDrivers,
    canManageTransporters,
    userProfile,
  } = usePermissions();

  // Check if user has any permission to view this page
  const hasAnyPermission = canAny([
    PERMISSIONS.CAN_VIEW_SETTINGS,
    PERMISSIONS.CAN_EDIT_SETTINGS,
    PERMISSIONS.CAN_CREATE_JOB,
    PERMISSIONS.CAN_EDIT_JOB,
    PERMISSIONS.CAN_DELETE_JOB,
    PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER,
    PERMISSIONS.CAN_CRUD_TRANSPORTER,
  ]);

  // Show debug information if no permissions
  if (!hasAnyPermission) {
    return (
      <div className="permission-page p-[20px]">
        <div className="permission-container">
          <div className="card debug-info">
            <div className="card-header">
              <h2>🔍 Permission System Debug</h2>
            </div>
            <div className="card-body">
              <div className="debug-content">
                <div className="alert alert-warning">
                  <h3>No Permissions Found</h3>
                  <p>
                    You don't have any of the required permissions to view this
                    page.
                  </p>
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <div className="label">User Profile Status</div>
                    <div className="value">
                      {userProfile ? "✅ Loaded" : "❌ Not loaded"}
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="label">User Role</div>
                    <div className="value">
                      {userProfile?.role?.name || "Unknown"}
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="label">Organization</div>
                    <div className="value">
                      {userProfile?.org?.name || "Unknown"}
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="label">Total Privileges</div>
                    <div className="value">
                      {userProfile?.role?.privileges?.length || 0}
                    </div>
                  </div>
                </div>

                {userProfile?.role?.privileges &&
                  userProfile.role.privileges.length > 0 && (
                    <div className="privileges-section">
                      <h4>Available Privileges</h4>
                      <div className="privileges-grid">
                        {userProfile.role.privileges.map((priv, index) => (
                          <div key={index} className="privilege-item">
                            <span className="privilege-name">{priv.name}</span>
                            <span className="privilege-type">{priv.type}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="permission-page p-[20px]">
      <div className="permission-container">
        {/* User Profile Section */}
        <PermissionGuard
          requiredPermissions={[PERMISSIONS.CAN_VIEW_SETTINGS]}
          fallback={
            <div className="card profile-info">
              <div className="card-header">
                <h2>👤 Current User Profile</h2>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  You don't have permission to view user profile information.
                </div>
              </div>
            </div>
          }
        >
          <div className="card profile-info">
            <div className="card-header">
              <h2>👤 Current User Profile</h2>
            </div>
            <div className="card-body">
              <div className="info-grid">
                <div className="info-item">
                  <div className="label">Full Name</div>
                  <div className="value">
                    {userProfile?.userFullName || "N/A"}
                  </div>
                </div>
                <div className="info-item">
                  <div className="label">Role</div>
                  <div className="value role-badge">
                    {userProfile?.role?.name || "N/A"}
                  </div>
                </div>
                <div className="info-item">
                  <div className="label">Organization</div>
                  <div className="value">{userProfile?.org?.name || "N/A"}</div>
                </div>
                <div className="info-item">
                  <div className="label">Total Permissions</div>
                  <div className="value">
                    {userProfile?.role?.privileges?.length || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>

        {/* Basic Permission Checks */}
        <PermissionGuard
          requiredAnyPermissions={[
            PERMISSIONS.CAN_CREATE_JOB,
            PERMISSIONS.CAN_EDIT_JOB,
            PERMISSIONS.CAN_DELETE_JOB,
          ]}
          fallback={
            <div className="card permission-checks">
              <div className="card-header">
                <h2>🔐 Basic Permission Checks</h2>
              </div>
              <div className="card-body">
                <div className="alert alert-warning">
                  You don't have any job management permissions to view this
                  section.
                </div>
              </div>
            </div>
          }
        >
          <div className="card permission-checks">
            <div className="card-header">
              <h2>🔐 Basic Permission Checks</h2>
            </div>
            <div className="card-body">
              <div className="permissions-grid">
                <div className="permission-item">
                  <div className="permission-icon">
                    {canCreateJob() ? "✅" : "❌"}
                  </div>
                  <div className="permission-details">
                    <div className="permission-name">Create Job</div>
                    <div className="permission-status">
                      {canCreateJob() ? "Allowed" : "Denied"}
                    </div>
                  </div>
                </div>
                <div className="permission-item">
                  <div className="permission-icon">
                    {canEditJob() ? "✅" : "❌"}
                  </div>
                  <div className="permission-details">
                    <div className="permission-name">Edit Job</div>
                    <div className="permission-status">
                      {canEditJob() ? "Allowed" : "Denied"}
                    </div>
                  </div>
                </div>
                <div className="permission-item">
                  <div className="permission-icon">
                    {canDeleteJob() ? "✅" : "❌"}
                  </div>
                  <div className="permission-details">
                    <div className="permission-name">Delete Job</div>
                    <div className="permission-status">
                      {canDeleteJob() ? "Allowed" : "Denied"}
                    </div>
                  </div>
                </div>
                <div className="permission-item">
                  <div className="permission-icon">
                    {canManageJobs() ? "✅" : "❌"}
                  </div>
                  <div className="permission-details">
                    <div className="permission-name">Manage Jobs</div>
                    <div className="permission-status">
                      {canManageJobs() ? "Allowed" : "Denied"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>

        {/* Show/Hide Examples */}
        <div className="card show-hide-examples">
          <div className="card-header">
            <h2>👁️ Show/Hide Examples</h2>
          </div>
          <div className="card-body">
            <div className="examples-grid">
              {/* Simple show/hide */}
              <PermissionGuard
                requiredPermissions={[PERMISSIONS.CAN_CREATE_JOB]}
                fallback={
                  <div className="example-item disabled">
                    <div className="example-icon">🚫</div>
                    <div className="example-content">
                      <h4>Create Job Button</h4>
                      <p>Hidden - you don't have permission</p>
                    </div>
                  </div>
                }
              >
                <div className="example-item active">
                  <div className="example-icon">✅</div>
                  <div className="example-content">
                    <h4>Create Job Button</h4>
                    <button className="demo-button primary">
                      Create New Job
                    </button>
                  </div>
                </div>
              </PermissionGuard>

              {/* Multiple permissions (AND logic) */}
              <PermissionGuard
                requiredAllPermissions={[
                  PERMISSIONS.CAN_EDIT_JOB,
                  PERMISSIONS.CAN_DELETE_JOB,
                ]}
                fallback={
                  <div className="example-item disabled">
                    <div className="example-icon">🚫</div>
                    <div className="example-content">
                      <h4>Advanced Job Management</h4>
                      <p>Hidden - you need both edit and delete permissions</p>
                    </div>
                  </div>
                }
              >
                <div className="example-item active">
                  <div className="example-icon">✅</div>
                  <div className="example-content">
                    <h4>Advanced Job Management</h4>
                    <div className="button-group">
                      <button className="demo-button secondary">
                        Edit Job
                      </button>
                      <button className="demo-button danger">Delete Job</button>
                    </div>
                  </div>
                </div>
              </PermissionGuard>

              {/* Any permission (OR logic) */}
              <PermissionGuard
                requiredAnyPermissions={[
                  PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER,
                  PERMISSIONS.CAN_CRUD_TRANSPORTER,
                ]}
                fallback={
                  <div className="example-item disabled">
                    <div className="example-icon">🚫</div>
                    <div className="example-content">
                      <h4>Driver/Transporter Management</h4>
                      <p>
                        Hidden - you need either driver or transporter
                        permissions
                      </p>
                    </div>
                  </div>
                }
              >
                <div className="example-item active">
                  <div className="example-icon">✅</div>
                  <div className="example-content">
                    <h4>Driver/Transporter Management</h4>
                    <div className="button-group">
                      <button className="demo-button purple">
                        Manage Drivers
                      </button>
                      <button className="demo-button indigo">
                        Manage Transporters
                      </button>
                    </div>
                  </div>
                </div>
              </PermissionGuard>
            </div>
          </div>
        </div>

        {/* Enable/Disable Examples */}
        <PermissionGuard
          requiredAnyPermissions={[
            PERMISSIONS.CAN_EDIT_JOB,
            PERMISSIONS.CAN_DELETE_JOB,
            PERMISSIONS.CAN_CREATE_JOB,
          ]}
          fallback={
            <div className="card enable-disable-examples">
              <div className="card-header">
                <h2>⚙️ Enable/Disable Examples</h2>
              </div>
              <div className="card-body">
                <div className="alert alert-warning">
                  You don't have any job management permissions to view this
                  section.
                </div>
              </div>
            </div>
          }
        >
          <div className="card enable-disable-examples">
            <div className="card-header">
              <h2>⚙️ Enable/Disable Examples</h2>
            </div>
            <div className="card-body">
              <div className="examples-grid">
                {/* Disabled with tooltip */}
                <PermissionGuard
                  requiredPermissions={[PERMISSIONS.CAN_EDIT_JOB]}
                  mode="disable"
                  showTooltip={true}
                  tooltipMessage="You need edit permission to modify this job"
                >
                  <div className="example-item">
                    <div className="example-icon">✏️</div>
                    <div className="example-content">
                      <h4>Edit Job Details</h4>
                      <button className="demo-button secondary">
                        Edit Job Details
                      </button>
                    </div>
                  </div>
                </PermissionGuard>

                {/* Disabled without tooltip */}
                <PermissionGuard
                  requiredPermissions={[PERMISSIONS.CAN_DELETE_JOB]}
                  mode="disable"
                >
                  <div className="example-item">
                    <div className="example-icon">🗑️</div>
                    <div className="example-content">
                      <h4>Delete Job</h4>
                      <button className="demo-button danger">Delete Job</button>
                    </div>
                  </div>
                </PermissionGuard>

                {/* Complex form */}
                <div className="example-item form-example">
                  <div className="example-icon">📝</div>
                  <div className="example-content">
                    <h4>Job Management Form</h4>
                    <div className="form-group">
                      <input
                        type="text"
                        placeholder="Job Title"
                        className="form-input"
                      />
                    </div>
                    <PermissionGuard
                      requiredPermissions={[PERMISSIONS.CAN_EDIT_JOB]}
                      mode="disable"
                      showTooltip={true}
                      tooltipMessage="You need edit permission to modify job details"
                    >
                      <div className="form-group">
                        <textarea
                          placeholder="Job Description"
                          className="form-input"
                          rows={3}
                        />
                      </div>
                    </PermissionGuard>
                    <PermissionGuard
                      requiredPermissions={[PERMISSIONS.CAN_CREATE_JOB]}
                      mode="disable"
                      showTooltip={true}
                      tooltipMessage="You need create permission to save this job"
                    >
                      <button className="demo-button primary">Save Job</button>
                    </PermissionGuard>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>

        {/* Conditional Content Examples */}
        <div className="card conditional-content">
          <div className="card-header">
            <h2>🎯 Conditional Content Examples</h2>
          </div>
          <div className="card-body">
            <div className="examples-grid">
              {/* Job Actions */}
              <div className="example-item">
                <div className="example-icon">🎛️</div>
                <div className="example-content">
                  <h4>Job Actions</h4>
                  <div className="action-buttons">
                    {canCreateJob() && (
                      <button className="action-button create">
                        + Create Job
                      </button>
                    )}
                    {canEditJob() && (
                      <button className="action-button edit">
                        ✏️ Edit Job
                      </button>
                    )}
                    {canDeleteJob() && (
                      <button className="action-button delete">
                        🗑️ Delete Job
                      </button>
                    )}
                    {!canCreateJob() && !canEditJob() && !canDeleteJob() && (
                      <div className="no-actions">
                        You have read-only access to jobs
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Available Sections */}
              <div className="example-item">
                <div className="example-icon">📋</div>
                <div className="example-content">
                  <h4>Available Sections</h4>
                  <div className="sections-grid">
                    {canManageJobs() && (
                      <div className="section-item jobs">
                        📋 Jobs Management
                      </div>
                    )}
                    {canManageDrivers() && (
                      <div className="section-item drivers">
                        👥 Drivers Management
                      </div>
                    )}
                    {canManageTransporters() && (
                      <div className="section-item transporters">
                        🚛 Transporters Management
                      </div>
                    )}
                    {!canManageJobs() &&
                      !canManageDrivers() &&
                      !canManageTransporters() && (
                        <div className="no-sections">
                          No management sections available
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Permission Patterns */}
        <PermissionGuard
          requiredPermissions={[PERMISSIONS.CAN_VIEW_SETTINGS]}
          fallback={
            <div className="card advanced-patterns">
              <div className="card-header">
                <h2>🚀 Advanced Permission Patterns</h2>
              </div>
              <div className="card-body">
                <div className="alert alert-warning">
                  You don't have permission to view advanced permission
                  patterns.
                </div>
              </div>
            </div>
          }
        >
          <div className="card advanced-patterns">
            <div className="card-header">
              <h2>🚀 Advanced Permission Patterns</h2>
            </div>
            <div className="card-body">
              <div className="examples-grid">
                {/* Role-based content */}
                <div className="example-item">
                  <div className="example-icon">👑</div>
                  <div className="example-content">
                    <h4>Role-Based Features</h4>
                    {userProfile?.role.name === "ROLE_FEDERATION_ADMIN" && (
                      <div className="role-features federation">
                        <h5>Federation Admin Features:</h5>
                        <ul>
                          <li>Manage all organizations</li>
                          <li>System-wide reports</li>
                          <li>Global settings</li>
                        </ul>
                      </div>
                    )}
                    {userProfile?.role.name === "ROLE_TRANSPORTER_ADMIN" && (
                      <div className="role-features transporter">
                        <h5>Transporter Admin Features:</h5>
                        <ul>
                          <li>Manage own transporters</li>
                          <li>Driver assignments</li>
                          <li>Fleet management</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Data access levels */}
                <div className="example-item">
                  <div className="example-icon">🔓</div>
                  <div className="example-content">
                    <h4>Data Access Levels</h4>
                    <div className="access-levels">
                      <div className="access-item">
                        <div className="access-icon">
                          {canCreateJob() ? "🔓" : "🔒"}
                        </div>
                        <div className="access-details">
                          <div className="access-name">Create</div>
                          <div className="access-status">
                            {canCreateJob() ? "Allowed" : "Denied"}
                          </div>
                        </div>
                      </div>
                      <div className="access-item">
                        <div className="access-icon">
                          {canEditJob() ? "🔓" : "🔒"}
                        </div>
                        <div className="access-details">
                          <div className="access-name">Edit</div>
                          <div className="access-status">
                            {canEditJob() ? "Allowed" : "Denied"}
                          </div>
                        </div>
                      </div>
                      <div className="access-item">
                        <div className="access-icon">
                          {canDeleteJob() ? "🔓" : "🔒"}
                        </div>
                        <div className="access-details">
                          <div className="access-name">Delete</div>
                          <div className="access-status">
                            {canDeleteJob() ? "Allowed" : "Denied"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>
      </div>
    </div>
  );
};

export default PermissionExamples;
